/*
  HS Kampagnen-Themenblock, Wischen auf dem Telefon
  Gehört zu sections/hs-sale-block.liquid und assets/hs-sale.css.

  Auf dem Telefon steht das Kachelraster als waagerechte Leiste statt als
  zweispaltiges Raster. Dass man wischen kann, zeigen zwei Dinge: die nächste
  Kachel schaut am Rand herein, und unter der Leiste stehen Punkte, einer je
  Kachel. Der Punkt des Ausschnitts, in dem man gerade steht, ist gefüllt.

  Die Punkte baut dieses Skript, nicht Liquid. Grund: eine Kachel kann beim
  Rendern wegfallen, etwa wenn das Produkt im aktuellen Land gesperrt ist
  (snippets/geo-hidden.liquid). Aus Liquid gezählt stimmte die Zahl der Punkte
  dann nicht mit der Zahl der Kacheln überein.

  Ohne Javascript bleibt die Leiste eine gewöhnliche Scrollfläche. Sie wischt
  weiterhin, nur ohne Punkte, und die hereinschauende Kachel zeigt weiter an,
  dass es zur Seite weitergeht.
*/

class HsSaleCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.hs-sale-block__grid');
    this.dots = this.querySelector('.hs-sale-block__dots');
    if (!this.scroller || !this.dots) return;

    this.cards = Array.from(this.scroller.children);
    if (this.cards.length < 2) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onScroll = this.schedule.bind(this, this.sync.bind(this));

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

    // Die Leiste ist nur unterhalb von 768px eine Leiste. Ein Wechsel der
    // Fensterbreite und nachgeladene Schriften ändern ihre Maße, ohne dass
    // ein Scroll-Ereignis fällt.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.onScroll);
      this.observer.observe(this.scroller);
    }

    this.build();
    this.sync();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  schedule(work) {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      work();
    });
  }

  build() {
    const fragment = document.createDocumentFragment();
    this.buttons = this.cards.map((card, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hs-sale-block__dot';
      const label = this.dots.dataset.label || 'Zu Kachel %index% von %total%';
      button.setAttribute(
        'aria-label',
        label.replace('%index%', index + 1).replace('%total%', this.cards.length)
      );
      button.addEventListener('click', () => this.goTo(index));
      fragment.appendChild(button);
      return button;
    });
    this.dots.appendChild(fragment);
  }

  // Scrollposition, an der Kachel i am linken Rand einrastet. Gerechnet wird
  // relativ zur ersten Kachel: die liegt um den Innenabstand der Leiste
  // eingerückt, und genau um den ist auch scroll-padding-inline versetzt.
  // Damit stimmt die Rechnung, ohne scroll-padding auszulesen.
  offsetOf(index) {
    const frame = this.scroller.getBoundingClientRect();
    const within = (el) => el.getBoundingClientRect().left - frame.left + this.scroller.scrollLeft;
    return within(this.cards[index]) - within(this.cards[0]);
  }

  goTo(index) {
    // Begrenzen auf das, was die Leiste hergibt. Die Einrastposition der
    // letzten Kachel liegt hinter dem Ende der Leiste, sie rastet deshalb an
    // der rechten Kante ein (assets/hs-sale.css). Ungekappt fragte der Klick
    // nach einer Position, die es nicht gibt.
    const max = this.scroller.scrollWidth - this.scroller.clientWidth;
    this.scroller.scrollTo({
      left: Math.max(0, Math.min(this.offsetOf(index), max)),
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  sync() {
    // Ein Pixel Toleranz, weil Browser bei fraktionalen Breiten rundet.
    const scrollable = this.scroller.scrollWidth - this.scroller.clientWidth > 1;
    this.dots.hidden = !scrollable;
    if (!scrollable) return;

    const position = this.scroller.scrollLeft;
    let current = 0;

    // Am Ende der Leiste gewinnt die letzte Kachel. Sonst bliebe sie
    // unmarkiert, weil die Leiste vorher zu Ende ist.
    if (position >= this.scroller.scrollWidth - this.scroller.clientWidth - 1) {
      current = this.cards.length - 1;
    } else {
      let shortest = Infinity;
      this.cards.forEach((card, index) => {
        const distance = Math.abs(this.offsetOf(index) - position);
        if (distance < shortest) {
          shortest = distance;
          current = index;
        }
      });
    }

    if (current === this.current) return;
    this.current = current;

    this.buttons.forEach((button, index) => {
      if (index === current) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('hs-sale-carousel')) {
  customElements.define('hs-sale-carousel', HsSaleCarousel);
}
